import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Typography } from '@mui/material';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const products = [
  {
    name: 'Certify',
    to: '/certify',
    lead: 'Adjudication support for ',
    strong: 'DBE and ACDBE certification',
    rest: ". Reads personal narratives and supporting documentation, applies your UCP's rubric, and returns a recommendation with the governing citation attached. No IT integration.",
    cite: '49 CFR Parts 26 and 23 · State DOTs, transit agencies, airport sponsors',
  },
  {
    name: 'Trade',
    to: '/trade',
    lead: 'Independent derivation for ',
    strong: 'US formal import entries',
    rest: '. Derives the Chapter 99 code chain your entry facts require and compares it, line by line, against what your broker declared. The model never decides what you owe.',
    cite: 'HTSUS Chapters 98 and 99 · Entry Type 01 · Importers of record',
  },
  {
    name: 'Pay',
    to: '/pay',
    lead: "Cross-border business payments settled in the ",
    strong: "supplier's local currency",
    rest: '. For US companies whose overseas suppliers invoice in anything other than USD. Full KYB, disclosed pricing, scope stated on the page.',
    cite: 'Currencycloud rails · US SMB and mid-market importers',
  },
];

const pillars = [
  {
    title: 'Built to the citation',
    body: 'Each product maps to a named body of regulation rather than pointing a general-purpose model at a pile of documents. Certify to 49 CFR Parts 26 and 23. Trade to the HTSUS. Pay to the permissible-use scope of a sponsored banking agreement.',
  },
  {
    title: 'No IT integration',
    body: 'Our products run alongside the systems you already have. No middleware, no migration, and no procurement cycle for an integration your IT team never budgeted for. Replacing a system of record is a multi-year project; the compliance problem is a present one.',
  },
  {
    title: 'Auditable by design',
    body: 'Every output traces to the rule it applies and the input it read. Source text is retained in the words the regulator published. Regulated decisions have to survive review, so our products show their work.',
  },
];

const frameworks = [
  '49 CFR Part 26 — DBE',
  '49 CFR Part 23 — ACDBE',
  '19 CFR',
  'HTSUS Chapters 98 & 99',
];

const HomePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleProductClick = (product) => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    navigate(product.to);
  };

  return (
    <Box className="home-page">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className="home-hero">
        <Box className="home-hero__grid-bg" />
        <Container maxWidth="lg">
          <Box className="home-hero__content">
            <Typography variant="h1" className="home-hero__title">
              AI for regulated commerce.
            </Typography>
            <Typography className="home-hero__subtitle">
              LUSID builds compliance-grade software for the points where regulation meets
              transaction: government certification, import classification, and cross-border
              payment. Three products, each built to a specific body of rules.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Product routing ──────────────────────────────────── */}
      <Box className="home-section" id="home-products">
        <Container maxWidth="lg">
          <Box className="home-products">
            {products.map((product) => (
              <Box
                key={product.name}
                className="home-product"
                role="link"
                tabIndex={0}
                onClick={() => handleProductClick(product)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleProductClick(product);
                  }
                }}
              >
                <Box className="home-product__name">
                  <span className="home-product__house">LUSID</span> {product.name}
                </Box>
                <Box>
                  <Typography className="home-product__desc">
                    {product.lead}
                    <strong>{product.strong}</strong>
                    {product.rest}
                  </Typography>
                  <Typography className="home-product__cite">{product.cite}</Typography>
                </Box>
                <Box className="home-product__go" aria-hidden="true">→</Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Thesis ───────────────────────────────────────────── */}
      <Box className="home-thesis">
        <Box className="home-thesis__grid-bg" />
        <Container maxWidth="lg">
          <Box className="home-thesis__content">
            <Typography className="home-thesis__overline">Regulatory engineering</Typography>
            <Typography variant="h2" className="home-thesis__title">
              In the physical economy, compliance is where the money stops.
            </Typography>
            <Typography className="home-thesis__body">
              Goods arrive at a border. A firm applies to be certified. A subcontractor finishes the
              work. In each case a regulated document stands between the work performed and the money
              settled — an entry summary, a personal narrative, a payment instruction — and while
              that document is wrong, incomplete, or waiting in a queue, so is the money.
            </Typography>
            <Typography className="home-thesis__body">
              That gap is not a software problem in general. It is a different problem in each
              regime, with its own rules, its own evidentiary standard, and its own reviewer to
              satisfy. <strong>LUSID builds one product per regime</strong> — for the agencies that
              certify, the importers that clear, and the businesses that pay.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Approach ─────────────────────────────────────────── */}
      <Box className="home-section" id="home-approach">
        <Container maxWidth="lg">
          <Box className="home-section__head">
            <Typography className="home-section__overline">Approach</Typography>
            <Typography variant="h2" className="home-section__title">
              Three regulatory regimes, one way of working
            </Typography>
            <Typography className="home-section__lede">
              Certify, Trade, and Pay serve different buyers under different rules, and we do not
              pretend otherwise. What they share is a method, and it is the reason a compliance
              officer can put any of them in front of an auditor.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {pillars.map(({ title, body }) => (
              <Grid item xs={12} md={4} key={title}>
                <Box className="home-pillar">
                  <Typography className="home-pillar__title">{title}</Typography>
                  <Typography className="home-pillar__body">{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Operating frameworks ─────────────────────────────── */}
      <Box className="home-section home-section--tinted">
        <Container maxWidth="lg">
          <Box className="home-section__head">
            <Typography className="home-section__overline">Operating frameworks</Typography>
            <Typography variant="h2" className="home-section__title">
              The rules our products are built against
            </Typography>
          </Box>
          <Box component="ul" className="home-frameworks">
            {frameworks.map((f) => (
              <Box component="li" key={f} className="home-frameworks__item">{f}</Box>
            ))}
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default HomePage;
